// Event settlement (spec §28, ADR 0010): a MANAGEMENT REPORT of what an
// event brought in and cost, built from the apps' own figures plus lines
// staff add. It is not accounting. QuickBooks stays the ledger (rule 3):
// nothing here is an invoice, a bill, a payment or a journal entry, and
// nothing here is posted anywhere.
//
// Money is integer cents in USD. Every line keeps where it came from: a
// snapshot of one app figure (with that app's "as of" time) or a manual
// entry by a named person.

import type { Sourced } from './sources'
import { readTicketingSettlementFigures, type TicketingSettlementFigures } from './source-settlement'
import type { BookingDealTerms } from './source-events'
import { describeDeal, LEAD_PARTY_KEY, type DealPnl, type DealStatement, type DealTreatment, type PartyStatement, type SettlementDealTerms } from './settlement-deal'
import {
  readBookingMoney,
  readPosMoney,
  readTicketingMoney,
  type BookingEventSummary,
  type EventSourceService,
  type PosEventSummary,
  type TicketingEventSummary,
} from './source-events'

// ------------------------------------------------------------ sign convention

// How a line counts, by its category's kind:
//
//   revenue    adds to gross revenue
//   deduction  comes off gross revenue (refunds, revenue share paid out)
//   cost       a direct cost of the event
//   memo       shown for reference only; never in any total (comps given
//              away, tips that belong to staff, cash collected against an
//              invoice already counted, a figure that mixes in sales tax)
//   held       collected for someone else (a promoter fee BTownTix holds
//              for the promoter): not BRE revenue, in no P&L total, shown
//              as "held for others" (ADR 0039)
//
// A line's amount is entered as the category reads: a $500 talent fee is
// +50000 in `talent`, a $200 refund is +20000 in `refunds`. A negative
// amount reverses within its category (a vendor credit is -5000 in
// `supplies`). Then:
//
//   gross revenue  = sum of revenue lines
//   net revenue    = gross revenue - sum of deduction lines
//   costs          = sum of cost lines
//   net            = net revenue - costs        (positive = the event made money)
//   held           = sum of held lines           (not BRE's money)
//
// BRE doesn't charge sales tax (owner, 2026-10-14): no category is tax, and
// a tax an app reports becomes a warning on its pull, never a line.
export const SIGN_CONVENTION_VERSION = 1

export const SIGN_CONVENTION_TEXT =
  'Amounts are entered as each category reads (a cost or a refund is a positive number; a negative amount reverses within its category). ' +
  'Gross revenue = revenue lines. Net revenue = gross revenue minus deductions. Net = net revenue minus costs; positive means the event made money. Memo lines are in no total.'

export type SettlementKind = 'revenue' | 'deduction' | 'cost' | 'memo' | 'held'

export const SETTLEMENT_CATEGORIES = {
  ticket_revenue: { label: 'Ticket revenue', kind: 'revenue' },
  ticket_fees: { label: 'Ticket fees (facility, convenience)', kind: 'revenue' },
  fnb_merch: { label: 'Food & beverage and merch revenue', kind: 'revenue' },
  merch: { label: 'Merch sales', kind: 'revenue' },
  sponsorship: { label: 'Sponsorship revenue (invoiced)', kind: 'revenue' },
  rental: { label: 'Rental revenue', kind: 'revenue' },
  other_revenue: { label: 'Other revenue', kind: 'revenue' },
  refunds: { label: 'Refunds', kind: 'deduction' },
  revenue_share: { label: 'Revenue share paid out', kind: 'deduction' },
  fnb_refunds: { label: 'Food & beverage refunds', kind: 'deduction' },
  talent: { label: 'Talent', kind: 'cost' },
  production: { label: 'Production', kind: 'cost' },
  staffing: { label: 'Staffing and labor', kind: 'cost' },
  security: { label: 'Police and security', kind: 'cost' },
  supplies: { label: 'Supplies', kind: 'cost' },
  marketing: { label: 'Marketing', kind: 'cost' },
  other_cost: { label: 'Other direct cost', kind: 'cost' },
  processing: { label: 'Card processing', kind: 'cost' },
  comps_value: { label: 'Comps given (value)', kind: 'memo' },
  tips: { label: 'Tips (not revenue)', kind: 'memo' },
  sponsorship_collected: { label: 'Sponsorship collected', kind: 'memo' },
  donations: { label: 'Donations (not event revenue)', kind: 'memo' },
  memo: { label: 'Other memo', kind: 'memo' },
  fees_for_others: { label: 'Buyer fees held for others', kind: 'held' },
  other_held: { label: 'Other money held for others', kind: 'held' },
} as const satisfies Record<string, { label: string; kind: SettlementKind }>

export type SettlementCategory = keyof typeof SETTLEMENT_CATEGORIES
export const SETTLEMENT_CATEGORY_KEYS = Object.keys(SETTLEMENT_CATEGORIES) as SettlementCategory[]
export const isSettlementCategory = (value: unknown): value is SettlementCategory => typeof value === 'string' && value in SETTLEMENT_CATEGORIES

export type SettlementStatus = 'draft' | 'submitted' | 'approved' | 'rejected'
export type SettlementStage = 'preliminary' | 'final'

// --------------------------------------------------------- source figures

// One figure an app reported, as the settlement froze it.
export type SettlementSourceFigure = {
  key: string
  label: string
  category: SettlementCategory
  amountCents: number
}

// What one linked app said when figures were pulled into a settlement.
// `notReported` names figures a settlement would want that this app's
// summary doesn't carry: they are missing, never zero.
export type SettlementSourcePull = {
  sourceService: EventSourceService
  entityType: string
  sourceId: string
  title: string
  status: 'ok' | 'unavailable' | 'not_configured' | 'denied'
  asOf: string
  reason: string | null
  figures: SettlementSourceFigure[]
  notReported: string[]
  // ADR 0039 add-ons, all optional so older pulls still read.
  // The app's staff page for the record (drill-through).
  adminUrl?: string | null
  // BTownTix: paid and comp tickets and check-ins, from its settlement figures.
  counts?: { paidTickets: number; compTickets: number; checkIns: number }
  // BTownTix: true when its settlement figures said who keeps each fee.
  feeOwnership?: boolean
  // BTownTix: the event's accounting mode (BRE_PROMOTED, RENTAL, ...).
  accountingMode?: string | null
  // Booking: the deal terms it reported, to copy into a deal.
  dealTerms?: BookingDealTerms | null
  // Things a person should look at (a tax reported although BRE charges none).
  warnings?: string[]
}

// What each app's event summary (contract v1, without the optional money
// add-ons) does NOT carry that an event view or a settlement needs. Shown on
// screen as "not reported by <app>". An app that sends its money add-on
// reports these; notReportedFor() says what is still missing.
export const NOT_REPORTED: Record<EventSourceService, string[]> = {
  'bre-ticketing': ['Ticket fees', 'Refunds', 'Comps (count and value)', 'Net ticket revenue', 'Check-ins'],
  pos: ['Gross sales', 'Discounts', 'Comps', 'Tips', 'Sales tax', 'Refunds', 'Tender mix', 'Revenue excluding tax and tips'],
  booking: ['Show deal terms (guarantee, splits)', 'Rental charges', 'Sponsorship invoiced', 'Sponsorship collected', 'Sponsorship outstanding'],
}

type AnyEventSummary = TicketingEventSummary | PosEventSummary | BookingEventSummary

export function notReportedFor(service: EventSourceService, data: AnyEventSummary | null): string[] {
  if (!data) return NOT_REPORTED[service]
  if (service === 'bre-ticketing') return readTicketingMoney((data as TicketingEventSummary).totals.money) ? [] : NOT_REPORTED[service]
  if (service === 'pos') return readPosMoney((data as PosEventSummary).totals.money) ? [] : NOT_REPORTED[service]
  const booking = data as BookingEventSummary
  // Staffing is no longer Booking's to report (ADR 0024): its hours are in Hub shifts.
  return readBookingMoney(booking.money) ? [] : NOT_REPORTED.booking
}

const usd = (currency: string) => currency.toUpperCase() === 'USD'

// The ticket price lines in BTownTix's ledger: the door. A fee carved out
// of the face is booked as a fee line, so it is never in these.
const TICKET_PRICE_TYPES = new Set(['TICKET_SALES', 'TICKET_SALES_PAYABLE', 'DISCOUNT', 'COMP_ADJUSTMENT'])
// BTownTix's buyer fee types (its HUB_FEE_LINE_TYPES).
const TICKET_FEE_TYPES = new Set(['FACILITY_FEE', 'PROMOTER_FEE', 'ONLINE_CONVENIENCE_FEE', 'ORDER_FEE', 'DELIVERY_FEE', 'CUSTOM_FEE', 'PROCESSING_FEE_CHARGED', 'EXCHANGE_FEE', 'FLEXIBLE_TICKET_FEE', 'PROCESSING_OFFSET'])
const OWNER_WORDS: Record<string, string> = { RENTER: 'renter', PROMOTER: 'promoter', CO_PROMOTER: 'co-promoter', ARTIST: 'artist', CHARITY: 'charity', TAX_AUTHORITY: 'tax authority', OTHER: 'someone else' }
const ownerWord = (owner: string) => OWNER_WORDS[owner] ?? owner.toLowerCase()

const dollarsText = (cents: number) => `$${(cents / 100).toFixed(2)}`

// What BTownTix can't say without its settlement figures (an older
// BTownTix, or the read failing): shown as "not reported".
export const TICKETING_LEDGER_NOT_REPORTED = ['Who keeps each buyer fee', 'Card processing cost', 'Paid ticket count']

// The BTownTix figures from its ledger lines (ADR 0039): who keeps each fee,
// the processing cost BRE bore, and the door from the ticket price lines.
function ticketingFiguresFromLedger(f: TicketingSettlementFigures, add: (key: string, label: string, category: SettlementCategory, amountCents: number) => void, warnings: string[]) {
  const groups = new Map<string, { label: string; category: SettlementCategory; cents: number }>()
  const into = (key: string, label: string, category: SettlementCategory, cents: number) => {
    const g = groups.get(key) ?? { label, category, cents: 0 }
    g.cents += cents
    groups.set(key, g)
  }
  let door = 0
  let tax = 0
  for (const line of f.lines) {
    if (TICKET_PRICE_TYPES.has(line.lineType)) door += line.cents
    else if (TICKET_FEE_TYPES.has(line.lineType)) {
      if (line.ownerType === 'BRE') into(`fee:${line.lineType}`, `${line.label} kept by BRE`, 'ticket_fees', line.cents)
      else into(`fee_held:${line.lineType}:${line.ownerType}`, `${line.label} held for the ${ownerWord(line.ownerType)}`, 'fees_for_others', line.cents)
    } else if (line.lineType === 'TAX') tax += line.cents
    else if (line.lineType === 'DONATION') into('ticket_donations', 'Donations on ticket orders (not ticket revenue)', 'donations', line.cents)
    else if (line.lineType === 'STRIPE_PROCESSING_COST') into('processing', 'Card processing: Stripe fees on this event’s payments', 'processing', -line.cents)
    else if (line.lineType === 'CHARGEBACK' || line.lineType === 'DISPUTE_FEE') into('chargebacks', 'Chargebacks and dispute fees', 'other_cost', -line.cents)
    else if (line.lineType === 'TRADE_EXPENSE') into('trade', 'Trade comps (value given in trade)', 'memo', -line.cents)
    else if (line.family === 'COST') into(`cost:${line.lineType}`, line.label, 'other_cost', -line.cents)
    else if (line.ownerType !== 'BRE' && line.family === 'SALE') into(`held:${line.lineType}:${line.ownerType}`, `${line.label} held for the ${ownerWord(line.ownerType)}`, 'other_held', line.cents)
    else into(`other:${line.lineType}`, `${line.label} (ticket orders)`, 'other_revenue', line.cents)
  }
  add('ticket_gross', 'Ticket sales (the door): after discounts and refunds, before buyer fees', 'ticket_revenue', door)
  for (const [key, g] of groups) if (g.cents !== 0) add(key.slice(0, 60), g.label.slice(0, 200), g.category, g.cents)
  if (tax !== 0) warnings.push(`BTownTix recorded ${dollarsText(tax)} of sales tax on this event. BRE doesn’t charge sales tax: check the event’s tax settings in BTownTix.`)
  if (f.accountingMode === 'RENTAL') warnings.push('BTownTix books this event’s ticket money as payable to a renter (accounting mode RENTAL). The deal should be a rental.')
}

// The figures a settlement takes from one app's event summary. Only what the
// app states as structured money becomes a figure: a text fact is never
// parsed into an amount, and a figure in another currency is left out.
//
// With an app's money add-on, each figure lands where it counts once:
//   BTownTix  with its settlement figures (ADR 0039): the door is the ticket
//             price lines; each buyer fee is BRE's (ticket_fees) or held
//             for its owner (fees_for_others); the Stripe processing cost
//             is a cost; donations, refunds and comps are memo.
//             Without them: ticket revenue = grossCents (already net of
//             refunds); fees kept = ticket_fees; refunds, comps and
//             donations are memo, and who keeps each fee is not reported.
//   POS       F&B/merch = gross sales - discounts - comps (before refunds);
//             refunds of goods = fnb_refunds (deduction); card surcharge on
//             goods = other revenue; tips, the surcharge on tips, comps and
//             the charged total are memo. Sales tax is never a line (BRE
//             charges none): a reported tax is a warning.
//   Booking   rental = what the rental agreements price; sponsorship =
//             cash invoiced; collected, contracted and trade are memo; the
//             artist guarantee is a talent cost when it comes from a
//             contract, a memo when only from an offer. The deal terms ride
//             along for a person to copy into the settlement's deal.
export function settlementPullFrom(
  link: { sourceService: EventSourceService; entityType: string; sourceId: string; titleSnapshot: string },
  summary: Sourced<AnyEventSummary>,
  extra: { ticketingFigures?: Sourced<TicketingSettlementFigures> } = {},
): SettlementSourcePull {
  const base = { sourceService: link.sourceService, entityType: link.entityType, sourceId: link.sourceId, asOf: summary.asOf }
  if (summary.status !== 'ok') return { ...base, title: link.titleSnapshot, status: summary.status, reason: summary.reason, figures: [], notReported: NOT_REPORTED[link.sourceService] }
  const figures: SettlementSourceFigure[] = []
  const warnings: string[] = []
  const add = (key: string, label: string, category: SettlementCategory, amountCents: number) => figures.push({ key, label, category, amountCents })
  const pull: Partial<SettlementSourcePull> = { adminUrl: summary.data.event.adminUrl }
  let notReported = notReportedFor(link.sourceService, summary.data)
  if (link.sourceService === 'bre-ticketing') {
    const data = summary.data as TicketingEventSummary
    if (usd(data.totals.currency)) {
      const m = readTicketingMoney(data.totals.money)
      const f = extra.ticketingFigures?.status === 'ok' ? readTicketingSettlementFigures(extra.ticketingFigures.data) : null
      if (f) {
        ticketingFiguresFromLedger(f, add, warnings)
        pull.counts = { paidTickets: f.tickets.paid, compTickets: f.tickets.comps, checkIns: f.tickets.checkIns }
        pull.feeOwnership = true
        pull.accountingMode = f.accountingMode
        if (m) {
          if (m.refunds.totalCents !== 0) add('ticket_refunds', `Ticket refunds, ${m.refunds.count} (already out of the door and fees)`, 'memo', m.refunds.totalCents)
          if (m.comps.count !== 0 || m.comps.faceValueCents !== 0) add('ticket_comps', `Comp tickets, ${m.comps.count} (face value)`, 'comps_value', m.comps.faceValueCents)
        }
      } else {
        add('ticket_gross', 'Ticket sales at price paid (tickets still active, net of refunds)', 'ticket_revenue', data.totals.grossCents)
        if (m) {
          add('ticket_fees', 'Buyer fees kept (net of fee refunds)', 'ticket_fees', m.feesCents)
          if (m.refunds.totalCents !== 0) add('ticket_refunds', `Ticket refunds, ${m.refunds.count} (already out of ticket revenue and fees)`, 'memo', m.refunds.totalCents)
          if (m.comps.count !== 0 || m.comps.faceValueCents !== 0) add('ticket_comps', `Comp tickets, ${m.comps.count} (face value)`, 'comps_value', m.comps.faceValueCents)
          if (m.donationsCents !== 0) add('ticket_donations', 'Donations on ticket orders (not in ticket revenue)', 'memo', m.donationsCents)
        }
        notReported = [...notReported, ...TICKETING_LEDGER_NOT_REPORTED]
      }
    }
  } else if (link.sourceService === 'pos') {
    const data = summary.data as PosEventSummary
    if (usd(data.totals.currency)) {
      // What customers were charged, net of refunds, with tips still in it.
      // Not revenue, so a memo line.
      add('pos_charged_net', 'POS charged, net of refunds (includes tips and card surcharge)', 'memo', data.totals.totalCents)
      const m = readPosMoney(data.totals.money)
      if (m) {
        add('pos_sales', 'POS sales after discounts and comps (no tips or card surcharge)', 'fnb_merch', m.grossSalesCents - m.discountsCents - m.compsCents)
        add('pos_refunds', 'POS refunds of goods', 'fnb_refunds', m.refunds.goodsCents)
        const goodsSurcharge = m.cardFeesCents - m.tipCardFeesCents
        if (goodsSurcharge !== 0) add('pos_card_fees', 'POS card surcharge on goods (net of refunds)', 'other_revenue', goodsSurcharge)
        if (m.compsCents !== 0) add('pos_comps', 'POS comps (value)', 'comps_value', m.compsCents)
        if (m.tipsCents !== 0) add('pos_tips', 'POS tips (belong to staff)', 'tips', m.tipsCents)
        if (m.tipCardFeesCents !== 0) add('pos_tip_card_fees', 'POS card surcharge on tips', 'memo', m.tipCardFeesCents)
        if (m.salesTaxCents !== 0) warnings.push(`POS recorded ${dollarsText(m.salesTaxCents)} of sales tax. BRE doesn’t charge sales tax: check the POS tax settings.`)
      }
    }
  } else if (link.sourceService === 'booking') {
    const m = readBookingMoney((summary.data as BookingEventSummary).money)
    if (m) {
      pull.dealTerms = m.dealTerms
      if (m.rentals.count > 0 || m.rentals.chargedCents !== 0) add('rental_charged', `Rental charges, ${m.rentals.count} (agreement price)`, 'rental', m.rentals.chargedCents)
      if (m.rentals.collectedCents !== 0) add('rental_collected', 'Rental collected', 'memo', m.rentals.collectedCents)
      if (m.sponsorships.invoicedCents !== 0) add('sponsorship_invoiced', 'Sponsorship invoiced (cash)', 'sponsorship', m.sponsorships.invoicedCents)
      if (m.sponsorships.collectedCents !== 0) add('sponsorship_collected', 'Sponsorship collected', 'sponsorship_collected', m.sponsorships.collectedCents)
      const uninvoiced = m.sponsorships.contractedCents - m.sponsorships.invoicedCents
      if (uninvoiced > 0) add('sponsorship_uninvoiced', 'Sponsorship contracted, not yet invoiced (cash)', 'memo', uninvoiced)
      if (m.sponsorships.tradeValueCents !== 0) add('sponsorship_trade', 'Sponsorship trade (in-kind) value', 'memo', m.sponsorships.tradeValueCents)
      const deal = m.dealTerms
      if (deal && deal.guaranteeCents !== null) {
        if (deal.source === 'contract') add('artist_guarantee', `Artist guarantee (contract, ${deal.status})`, 'talent', deal.guaranteeCents)
        else add('artist_guarantee_offer', `Artist guarantee (offer, ${deal.status}; no contract yet)`, 'memo', deal.guaranteeCents)
      }
    }
  }
  if (warnings.length) pull.warnings = warnings
  return { ...base, title: summary.data.event.title, status: 'ok', reason: null, figures, notReported, ...pull }
}

// ------------------------------------------------------------------ totals

export type SettlementLineLike = { category: string; amountCents: number }

export type SettlementTotals = {
  currency: 'USD'
  grossRevenueCents: number
  deductionsCents: number
  netRevenueCents: number
  costsCents: number
  netCents: number
  memoCents: number
  // Collected for others (ADR 0039); not BRE's money, in no other total.
  heldCents: number
  byCategory: { category: SettlementCategory; label: string; kind: SettlementKind; totalCents: number; lineCount: number }[]
}

export function settlementTotals(lines: SettlementLineLike[]): SettlementTotals {
  const sums = new Map<SettlementCategory, { total: number; count: number }>()
  const byKind: Record<SettlementKind, number> = { revenue: 0, deduction: 0, cost: 0, memo: 0, held: 0 }
  for (const line of lines) {
    if (!isSettlementCategory(line.category)) throw new Error(`Unknown settlement category ${line.category}`)
    if (!Number.isSafeInteger(line.amountCents)) throw new Error('Settlement amounts are whole cents')
    const entry = sums.get(line.category) ?? { total: 0, count: 0 }
    entry.total += line.amountCents
    entry.count += 1
    sums.set(line.category, entry)
    byKind[SETTLEMENT_CATEGORIES[line.category].kind] += line.amountCents
  }
  const netRevenueCents = byKind.revenue - byKind.deduction
  return {
    currency: 'USD',
    grossRevenueCents: byKind.revenue,
    deductionsCents: byKind.deduction,
    netRevenueCents,
    costsCents: byKind.cost,
    netCents: netRevenueCents - byKind.cost,
    memoCents: byKind.memo,
    heldCents: byKind.held,
    // In category order, only categories that have lines.
    byCategory: SETTLEMENT_CATEGORY_KEYS.filter((key) => sums.has(key)).map((key) => ({
      category: key,
      label: SETTLEMENT_CATEGORIES[key].label,
      kind: SETTLEMENT_CATEGORIES[key].kind,
      totalCents: sums.get(key)!.total,
      lineCount: sums.get(key)!.count,
    })),
  }
}

// -------------------------------------------------------------- the record

export type SettlementLine = {
  id: string
  position: number
  origin: 'source' | 'manual'
  category: SettlementCategory
  label: string
  amountCents: number
  note: string | null
  // For a source line: which app figure, and when the app stated it.
  // 'hub-night': a night's approved figure on an overall settlement (ADR 0082).
  source: { sourceService: EventSourceService | 'hub-night'; entityType: string; sourceId: string; figureKey: string; asOf: string } | null
  createdByName: string | null
  createdAt: string
  updatedAt: string
  // ADR 0039: what an expense means for the deal, its receipt in the
  // document catalog (pinned to a version), and the catalog entry it came
  // from. Defaults: 'bre', none, none.
  dealTreatment: DealTreatment
  receipt: { documentId: string; version: number; title: string | null } | null
  expenseCategory: { source: 'booking' | 'hub'; ref: string } | null
  // ADR 0040: the party an expense belongs to (null: shared, or the lead's
  // when charged).
  dealPartyKey: string | null
}

// After approval (ADR 0039): sent to the counterparty, signed by them,
// payments recorded by hand, closed. Append-only; an approved version never
// changes, so these live beside it.
// A write-off (ADR 0040) settles a small difference left after the
// payments: it counts like a payment toward the balance, records why, and
// needs settlements.approve.
export type SettlementStepKind = 'sent' | 'countersigned' | 'payment' | 'write_off' | 'closed' | 'reopened'
export type SettlementStep = {
  id: string
  kind: SettlementStepKind
  // ADR 0040: which party a sent, signed, payment or write-off step is
  // about. Null: the lead (every step recorded before ADR 0040).
  partyKey: string | null
  // The approval snapshot hash the step is about: what was sent or signed.
  snapshotHash: string
  sentTo: string | null
  sentVia: string | null
  signerName: string | null
  signerTitle: string | null
  signedAt: string | null
  paymentDirection: 'bre_to_counterparty' | 'counterparty_to_bre' | null
  amountCents: number | null
  method: string | null
  reference: string | null
  paidOn: string | null
  note: string | null
  recordedByName: string | null
  createdAt: string
}

// draft -> review -> approved -> sent -> paid -> closed. Derived from the
// version's status and its steps; 'paid' when the recorded payments settle
// the amount due exactly (or nothing was due).
export type SettlementPhase = 'draft' | 'review' | 'rejected' | 'approved' | 'sent' | 'paid' | 'closed'
export const SETTLEMENT_PHASE_LABELS: Record<SettlementPhase, string> = {
  draft: 'Draft',
  review: 'In review',
  rejected: 'Rejected',
  approved: 'Approved',
  sent: 'Sent',
  paid: 'Paid',
  closed: 'Closed',
}

export type EventSettlement = {
  id: string
  eventId: string
  version: number
  revisesSettlementId: string | null
  stage: SettlementStage
  status: SettlementStatus
  currency: 'USD'
  note: string | null
  sources: SettlementSourcePull[]
  sourcesPulledAt: string | null
  lines: SettlementLine[]
  totals: SettlementTotals
  // The approval request that is waiting on it or decided it, if any.
  approvalId: string | null
  approvedAt: string | null
  createdByName: string | null
  createdAt: string
  updatedAt: string
  // ADR 0039. Null: no deal (a P&L-only settlement, as before).
  deal: SettlementDealTerms | null
  // The deal worked out from this version's frozen lines; null with no deal.
  // With several parties (ADR 0040), these are the lead's.
  statement: DealStatement | null
  // ADR 0040: every party with its terms, statement and balance, in billing
  // order. Empty with no deal.
  parties: (PartyStatement & { balance: PartyBalance })[]
  // ADR 0040: the P&L with the deal in it (each act's computed pay, not
  // Booking's contract figure). Null with no deal.
  dealPnl: DealPnl | null
  // Warnings the apps raised when their figures were pulled.
  warnings: string[]
  // Any linked app that didn't answer, or answered without its money:
  // the night's money is incomplete, never zero.
  incomplete: string[]
  steps: SettlementStep[]
  phase: SettlementPhase
  // The approval's snapshot hash, once approved: what the statement is.
  approvedSnapshotHash: string | null
}

// ------------------------------------------------------------------- export

const cell = (value: string | number | null): string => {
  if (value === null) return ''
  const text = String(value)
  // Quote anything a spreadsheet could misread, and defuse formulas.
  const formula = typeof value === 'string' && /^[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)
  const safe = formula ? `'${text}` : text
  return /[",\n\r]/.test(safe) || safe !== text ? `"${safe.replaceAll('"', '""')}"` : safe
}

const dollars = (cents: number) => {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

// A CSV of one settlement version: a header block that says what it is (and
// what it isn't), every line with its source, then the totals.
export function settlementCsv(s: EventSettlement, event: { name: string; startsOn: string; endsOn: string }): string {
  const rows: (string | number | null)[][] = [
    ['BRE Hub event settlement: a management report, not accounting. QuickBooks Online is the ledger.'],
    ['Event', event.name],
    ['Dates', `${event.startsOn} to ${event.endsOn}`],
    ['Version', s.version],
    ['Stage', s.stage],
    ['Status', s.status],
    ['Currency', s.currency],
    ['Sign convention', SIGN_CONVENTION_TEXT],
    [],
    ['Position', 'Category', 'Kind', 'Label', 'Amount (USD)', 'Amount (cents)', 'Origin', 'Source app', 'Source record', 'Figure', 'Source as of', 'Note'],
    ...s.lines.map((line) => [
      line.position,
      line.category,
      SETTLEMENT_CATEGORIES[line.category].kind,
      line.label,
      dollars(line.amountCents),
      line.amountCents,
      line.origin === 'source' ? 'App figure snapshot' : 'Manual',
      line.source?.sourceService ?? null,
      line.source ? `${line.source.entityType}:${line.source.sourceId}` : null,
      line.source?.figureKey ?? null,
      line.source?.asOf ?? null,
      line.note,
    ]),
    [],
    ['Total', 'Amount (USD)', 'Amount (cents)'],
    ['Gross revenue', dollars(s.totals.grossRevenueCents), s.totals.grossRevenueCents],
    ['Deductions', dollars(s.totals.deductionsCents), s.totals.deductionsCents],
    ['Net revenue', dollars(s.totals.netRevenueCents), s.totals.netRevenueCents],
    ['Costs', dollars(s.totals.costsCents), s.totals.costsCents],
    ['Net', dollars(s.totals.netCents), s.totals.netCents],
    ['Memo (in no total)', dollars(s.totals.memoCents), s.totals.memoCents],
    ['Held for others (not BRE money)', dollars(s.totals.heldCents), s.totals.heldCents],
    ...s.totals.byCategory.map((c) => [`Category: ${c.label}`, dollars(c.totalCents), c.totalCents]),
    [],
    ['Source', 'Record', 'Status', 'As of', 'Not reported by the app'],
    ...s.sources.map((src) => [src.sourceService, `${src.entityType}:${src.sourceId} ${src.title}`, src.status === 'ok' ? 'ok' : `${src.status}: ${src.reason ?? ''}`, src.asOf, src.notReported.join('; ')]),
    ...(s.parties.length ? s.parties : s.deal && s.statement ? [{ key: LEAD_PARTY_KEY, position: 1, terms: s.deal, statement: s.statement }] : []).flatMap((p) => [
      [],
      ['Deal with', p.terms.counterpartyName, p.terms.counterpartyRole],
      ['Deal', describeDeal(p.terms)],
      ['Rounding', p.statement.roundingRule],
      ['Step', 'How it was worked out', 'Toward the counterparty (USD)', 'Toward the counterparty (cents)', 'In the total'],
      ...p.statement.steps.map((st) => [st.label, st.explanation, st.amountCents === null ? 'MISSING' : dollars(st.amountCents), st.amountCents, st.role === 'due' ? 'yes' : 'no (working figure)']),
      [
        p.statement.amountDueCents === null ? 'Amount due: INCOMPLETE' : p.statement.amountDueCents >= 0 ? `BRE owes ${p.terms.counterpartyName}` : `${p.terms.counterpartyName} owes BRE`,
        p.statement.amountDueCents === null ? p.statement.missing.map((m) => `${m.label}: ${m.reason}`).join(' ') : '',
        p.statement.amountDueCents === null ? null : dollars(Math.abs(p.statement.amountDueCents)),
        p.statement.amountDueCents === null ? null : Math.abs(p.statement.amountDueCents),
      ],
    ]),
    ...(s.dealPnl?.totals
      ? [
          [],
          ['P&L with the deal (each act’s computed pay; the contract figure is memo)', 'Amount (USD)', 'Amount (cents)'],
          ...s.dealPnl.lines.map((l) => [`${SETTLEMENT_CATEGORIES[l.category].label}: ${l.label}`, dollars(l.amountCents), l.amountCents]),
          ...s.dealPnl.replaced.map((r) => [`Memo: ${r.label}`, dollars(r.amountCents), r.amountCents]),
          ['Net revenue', dollars(s.dealPnl.totals.netRevenueCents), s.dealPnl.totals.netRevenueCents],
          ['Costs', dollars(s.dealPnl.totals.costsCents), s.dealPnl.totals.costsCents],
          ['Net after the deal', dollars(s.dealPnl.totals.netCents), s.dealPnl.totals.netCents],
        ]
      : []),
  ]
  return rows.map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n'
}

// ------------------------------------------------------- completeness, phase

// Why the night's money is incomplete: each linked app that didn't answer,
// and each that answered without its money. Empty when complete.
export function settlementIncomplete(sources: SettlementSourcePull[]): string[] {
  const out: string[] = []
  const name = (s: SettlementSourcePull) => (s.sourceService === 'bre-ticketing' ? 'BTownTix' : s.sourceService === 'pos' ? 'POS' : 'Booking')
  for (const src of sources) {
    if (src.status !== 'ok') out.push(`${name(src)} didn’t answer for ${src.title}: ${(src.reason ?? src.status).replace(/\.$/, '')}.`)
    else if (src.sourceService !== 'booking' && src.notReported.length > 0) out.push(`${name(src)} didn’t report ${src.notReported.join(', ').toLowerCase()} for ${src.title}.`)
  }
  return out
}

type StepLike = Pick<SettlementStep, 'kind' | 'paymentDirection' | 'amountCents'> & { partyKey?: string | null }

// What each party is owed and what has settled it (ADR 0040).
export type PartyBalance = {
  // The statement's amount due (null while incomplete).
  dueCents: number | null
  // Payments recorded, net, toward the counterparty.
  paidCents: number
  // Write-offs recorded, net, toward the counterparty.
  writtenOffCents: number
  // What is still owed toward the counterparty: positive, BRE still owes
  // them; negative, they still owe BRE. Null while incomplete.
  remainingCents: number | null
}

const toward = (st: StepLike) => (st.paymentDirection === 'bre_to_counterparty' ? st.amountCents! : -st.amountCents!)
const ofParty = (steps: StepLike[], partyKey: string | undefined, leadKey: string) => (partyKey === undefined ? steps : steps.filter((st) => (st.partyKey ?? leadKey) === partyKey))

export function partyBalance(steps: StepLike[], dueCents: number | null, partyKey?: string, leadKey: string = LEAD_PARTY_KEY): PartyBalance {
  const mine = ofParty(steps, partyKey, leadKey)
  const paidCents = mine.filter((st) => st.kind === 'payment' && st.amountCents !== null).reduce((a, st) => a + toward(st), 0)
  const writtenOffCents = mine.filter((st) => st.kind === 'write_off' && st.amountCents !== null).reduce((a, st) => a + toward(st), 0)
  return { dueCents, paidCents, writtenOffCents, remainingCents: dueCents === null ? null : dueCents - paidCents - writtenOffCents }
}

// Where a version is, from its status and steps. dues undefined: the version
// has no deal, so it is never "paid", only sent and closed. A number (or
// null) is the one party's amount due; a list is every party's (ADR 0040).
export function settlementPhase(status: SettlementStatus, steps: StepLike[], dues: number | null | undefined | { key: string; amountDueCents: number | null }[], leadKey: string = LEAD_PARTY_KEY): SettlementPhase {
  if (status === 'draft') return 'draft'
  if (status === 'submitted') return 'review'
  if (status === 'rejected') return 'rejected'
  // The latest close or reopen decides whether it's closed.
  const lastToggle = [...steps].reverse().find((st) => st.kind === 'closed' || st.kind === 'reopened')
  if (lastToggle?.kind === 'closed') return 'closed'
  if (dues !== undefined) {
    const list = Array.isArray(dues) ? dues : [{ key: leadKey, amountDueCents: dues }]
    if (list.length > 0 && list.every((d) => settlementPaid(ofParty(steps, Array.isArray(dues) ? d.key : undefined, leadKey), d.amountDueCents))) return 'paid'
  }
  if (steps.some((st) => st.kind === 'sent' || st.kind === 'countersigned')) return 'sent'
  return 'approved'
}

// Net of what was recorded, toward the counterparty: BRE paying them is
// positive, them paying BRE negative (the amount due's sign). Write-offs
// (ADR 0040) count like payments.
export function settlementPaidCents(steps: StepLike[]): number {
  return steps.filter((st) => (st.kind === 'payment' || st.kind === 'write_off') && st.amountCents !== null).reduce((acc, st) => acc + toward(st), 0)
}

// Settled exactly, to the cent.
export function settlementPaid(steps: StepLike[], amountDueCents: number | null): boolean {
  if (amountDueCents === null) return false
  return settlementPaidCents(steps) === amountDueCents
}
